// src/features/plan/PolicyBand.jsx
//
// MARKET-PLAN-POLICY-BAND: Read-only collapsible band showing the active
// company capacity policy baseline. Sits between StrategyBand and the
// PlanMatrix on the Market Plan view.
//
// Props:
//   baseline        {object|null} — baseline object from getPolicyBaseline()
//   overrideCount   {number}      — count of offices with per-office overrides
//   isAdmin         {boolean}     — show "Manage in Admin →" button when true
//   masterExpanded  {object|null} — {expanded:bool, seq:number} from expand-all
//
// Toggle state is persisted in sessionStorage so it survives same-session
// navigation but resets on a new session.

import React, { useState, useEffect } from "react";
import { T } from "../../app/tokens.js";
import { PlanExpandCaret } from "./PlanExpandCaret.jsx";
import { SharingRatioValue } from "../../components/SharingRatioValue.jsx";

const SESSION_KEY = "plan:policyBandOpen";

// ─── Format helpers (exported for unit tests) ─────────────────────────────────

export function fmtDeskRatio(v) {
  if (v == null) return "—";
  return <SharingRatioValue numerator={Number(v).toFixed(1)} />;
}

export function fmtRtoDays(v) {
  if (v == null) return "—";
  return `${v}`;
}

export function fmtHotelingPct(v) {
  if (v == null) return "—";
  return `${v}%`;
}

export function fmtSfFloor(v) {
  if (v == null) return "—";
  return `${v} sf/seat`;
}

export function fmtHcBuffer(v) {
  if (v == null) return "—";
  return `${v}%`;
}

export function fmtConfGiveback(v) {
  if (v == null) return "—";
  return `${v}%`;
}

export function fmtTeamBlockWaste(v) {
  if (v == null) return "—";
  return `${v}%`;
}

export function fmtEffectiveDate(d) {
  if (!d) return null;
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch {
    return d;
  }
}

// ─── Inline chip (collapsed row) ──────────────────────────────────────────────

function ParamChip({ children, testId }) {
  return (
    <span
      data-testid={testId}
      style={{
        display:      "inline-flex",
        alignItems:   "center",
        borderRadius: 6,
        padding:      "2px 10px",
        fontSize:     T.fsXs,
        fontWeight:   T.fwSemibold,
        background:   T.white,
        color:        T.dark,
        border:       `1px solid ${T.policyBandCellBorder}`,
        whiteSpace:   "nowrap",
      }}
    >
      {children}
    </span>
  );
}

// ─── Collapsed header row ──────────────────────────────────────────────────────

function BandHeader({ baseline, overrideCount, isExpanded, onToggle }) {
  return (
    <div
      data-testid={isExpanded ? "policy-band-toggle-header" : "policy-band-collapsed"}
      style={{
        display:      "flex",
        alignItems:   "center",
        gap:          8,
        flexWrap:     "wrap",
        padding:      isExpanded ? "3px 16px" : "1px 16px",
        cursor:       "pointer",
        userSelect:   "none",
        borderBottom: isExpanded ? `1px solid ${T.policyBandBorder}` : "none",
      }}
      onClick={onToggle}
      role="button"
      tabIndex={0}
      aria-expanded={isExpanded}
      aria-label={isExpanded ? "Hide policy assumptions" : "Show policy assumptions"}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); }
      }}
    >
      {/* Label */}
      <span style={{
        fontSize:      T.fsXxs,
        fontWeight:    T.fwBold,
        letterSpacing: "0.07em",
        textTransform: "uppercase",
        color:         "var(--v2-accent)",
        flexShrink:    0,
      }}>
        ◆ POLICY · Baseline assumptions
      </span>

      {/* Collapsed chips — 5 most-important knobs; hidden when expanded */}
      {!isExpanded && baseline && (
        <>
          <ParamChip testId="policy-chip-rto-days">{fmtRtoDays(baseline.rtoDays)} days/mo</ParamChip>
          <ParamChip testId="policy-chip-desk-ratio">{fmtDeskRatio(baseline.deskRatio)}</ParamChip>
          <ParamChip testId="policy-chip-hoteling">{fmtHotelingPct(baseline.hotelingPct)} hoteling</ParamChip>
          <ParamChip testId="policy-chip-sf-floor">{fmtSfFloor(baseline.sfFloor)}</ParamChip>
          <ParamChip testId="policy-chip-hc-buffer">{fmtHcBuffer(baseline.hcBufferPct)} buffer</ParamChip>
          {overrideCount > 0 && (
            <span
              data-testid="policy-chip-overrides"
              style={{
                display:      "inline-flex",
                alignItems:   "center",
                borderRadius: 6,
                padding:      "2px 10px",
                fontSize:     T.fsXs,
                fontWeight:   T.fwSemibold,
                background:   T.blueSoft,
                color:        T.blue,
                whiteSpace:   "nowrap",
              }}
            >
              {overrideCount} {overrideCount === 1 ? "office" : "offices"} override
            </span>
          )}
        </>
      )}

      <div style={{ flex: 1, minWidth: 8 }} />

      {/* Caret + show/hide label */}
      <span style={{
        display:    "inline-flex",
        alignItems: "center",
        gap:        4,
        flexShrink: 0,
        whiteSpace: "nowrap",
      }}>
        <span style={{
          fontSize:   T.fsXs,
          fontWeight: T.fwSemibold,
          color:      "var(--v2-accent)",
        }}>
          {isExpanded ? "Hide" : "Show policy"}
        </span>
        <PlanExpandCaret isExpanded={isExpanded} />
      </span>
    </div>
  );
}

// ─── Individual knob grid cell ─────────────────────────────────────────────────

function KnobCell({ label, value, testId }) {
  return (
    <div
      data-testid={testId}
      style={{
        background:    T.white,
        border:        `1px solid ${T.policyBandCellBorder}`,
        borderRadius:  9,
        padding:       "10px 14px",
        display:       "flex",
        flexDirection: "column",
        gap:           4,
      }}
    >
      <span style={{
        fontSize:      T.fsXxs,
        color:         T.light,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        fontWeight:    T.fwBold,
      }}>
        {label}
      </span>
      <span style={{
        fontSize:   T.fsLg,
        color:      T.dark,
        fontWeight: T.fwSemibold,
        lineHeight: 1.2,
      }}>
        {value}
      </span>
    </div>
  );
}

// ─── 7 knobs in comp order ────────────────────────────────────────────────────

const PARAM_ROWS = [
  { label: "Days in office / mo",     key: "rtoDays",           fmt: fmtRtoDays        },
  { label: "Desk sharing",            key: "deskRatio",         fmt: fmtDeskRatio      },
  { label: "Hoteling / visitor hold", key: "hotelingPct",       fmt: fmtHotelingPct    },
  { label: "Conf-room give-back",     key: "confGivebackPct",   fmt: fmtConfGiveback   },
  { label: "Team-block waste",        key: "teamBlockWastePct", fmt: fmtTeamBlockWaste },
  { label: "Density floor",           key: "sfFloor",           fmt: fmtSfFloor        },
  { label: "Target buffer",           key: "hcBufferPct",       fmt: fmtHcBuffer       },
];

// ─── Expanded baseline grid ────────────────────────────────────────────────────
//
// 4-column grid: 7 knob cells fill rows 1–2 (slots 1–7); the "Manage in Admin →"
// button occupies slot 8 (last cell of row 2) when isAdmin is true.
// Override count is non-structural — shown as a badge in the footer so the
// 2-row grid structure is never disturbed.

function ExpandedBand({ baseline, overrideCount, isAdmin }) {
  if (!baseline) return null;

  const effectiveDateStr = fmtEffectiveDate(baseline.effectiveDate);

  return (
    <div data-testid="policy-band-expanded">
      {/* 4-column bordered cell grid — max 8 slots (7 knobs + admin button) */}
      <div style={{
        display:             "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        gap:                 10,
        padding:             "14px 16px 10px",
        borderBottom:        `1px solid ${T.policyBandBorder}`,
      }}>
        {PARAM_ROWS.map(({ label, key, fmt }) => (
          <KnobCell
            key={key}
            testId={`policy-param-${key}`}
            label={label}
            value={fmt(baseline[key])}
          />
        ))}

        {/* Admin button — fills 8th slot; rights-gated */}
        {isAdmin && (
          <div style={{
            display:        "flex",
            alignItems:     "center",
            justifyContent: "center",
            background:     T.white,
            border:         `1px solid ${T.policyBandCellBorder}`,
            borderRadius:   9,
            padding:        "10px 14px",
          }}>
            <a
              data-testid="policy-admin-link"
              href="/admin?section=policy"
              onClick={(e) => e.stopPropagation()}
              style={{
                display:        "inline-block",
                background:     "var(--v2-accent)",
                color:          T.white,
                borderRadius:   8,
                padding:        "8px 16px",
                fontSize:       T.fsXs,
                fontWeight:     T.fwSemibold,
                textDecoration: "none",
                whiteSpace:     "nowrap",
                textAlign:      "center",
              }}
            >
              Manage in Admin →
            </a>
          </div>
        )}
      </div>

      {/* Note + effective date + override chip footer */}
      <div style={{
        padding:        "10px 16px",
        fontSize:       T.fsXs,
        color:          T.mid,
        display:        "flex",
        justifyContent: "space-between",
        alignItems:     "center",
        gap:            12,
        flexWrap:       "wrap",
      }}>
        <span data-testid="policy-band-note">
          These assumptions drive the capacity / over-capacity / seats-needed math below.
        </span>

        <span style={{
          display:    "inline-flex",
          alignItems: "center",
          gap:        8,
          flexWrap:   "wrap",
          flexShrink: 0,
        }}>
          {overrideCount > 0 && (
            <span
              data-testid="policy-param-overrides"
              style={{
                display:      "inline-flex",
                alignItems:   "center",
                borderRadius: 6,
                padding:      "2px 10px",
                fontSize:     T.fsXs,
                fontWeight:   T.fwSemibold,
                background:   T.blueSoft,
                color:        T.blue,
                whiteSpace:   "nowrap",
              }}
            >
              {overrideCount} {overrideCount === 1 ? "office" : "offices"} override
            </span>
          )}
          {effectiveDateStr && (
            <span
              data-testid="policy-band-effective-date"
              style={{ whiteSpace: "nowrap", color: T.light }}
            >
              Effective {effectiveDateStr} · company baseline
            </span>
          )}
        </span>
      </div>
    </div>
  );
}

// ─── Exported PolicyBand ──────────────────────────────────────────────────────

export function PolicyBand({
  baseline = null,
  overrideCount = 0,
  isAdmin = false,
  masterExpanded = null,
}) {
  const [open, setOpen] = useState(() => {
    try { return sessionStorage.getItem(SESSION_KEY) === "true"; } catch { return false; }
  });

  function setAndPersist(value) {
    setOpen(value);
    try { sessionStorage.setItem(SESSION_KEY, String(value)); } catch { /* ignore */ }
  }

  // MARKET-PLAN-EXPAND-ALL: when the master expand-all fires, masterExpanded
  // changes to a new {expanded, seq} event object. seq increments on every
  // actuation so this effect fires even when polarity stays the same.
  // null = not set yet; skip on mount.
  useEffect(() => {
    if (masterExpanded === null) return;
    setAndPersist(masterExpanded.expanded);
  }, [masterExpanded]);

  // Render nothing until baseline data is available.
  if (!baseline) return null;

  return (
    <div
      data-testid="policy-band"
      style={{
        background:   T.policyBandBg,
        border:       `1px solid ${T.policyBandBorder}`,
        borderRadius: 12,
        boxShadow:    "0 1px 2px rgba(16,36,58,.05)",
        marginBottom: 12,
        overflow:     "hidden",
      }}
    >
      <BandHeader
        baseline={baseline}
        overrideCount={overrideCount}
        isExpanded={open}
        onToggle={() => setAndPersist(!open)}
      />

      {open && (
        <ExpandedBand
          baseline={baseline}
          overrideCount={overrideCount}
          isAdmin={isAdmin}
        />
      )}
    </div>
  );
}
